import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef
} from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  getDocs,
  updateDoc,
  doc,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import Sidebar from '../Components/Sidebar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import {
  FaBell,
  FaCheckCircle,
  FaInbox,
  FaArchive,
  FaStar,
  FaRegStar,
  FaSearch,
  FaSync,
  FaFilter,
  FaChevronDown,
  FaChevronUp,
  FaTrashAlt,
  FaExclamationCircle,
  FaExclamationTriangle,
  FaTimes,
  FaCompressArrowsAlt,
  FaExpandArrowsAlt,
  FaBolt,
  FaGlobeAmericas,
  FaLink
} from 'react-icons/fa';

import '../Style/Notifications.css';

/* ---------------- Utility Helpers ---------------- */
const RELATIVE_THRESHOLDS = [
  { s: 60, label: v => `${v}s` },               // < 60s
  { s: 3600, label: v => `${Math.floor(v/60)}m` }, // < 1h
  { s: 86400, label: v => `${Math.floor(v/3600)}h` }, // < 24h
  { s: 604800, label: v => `${Math.floor(v/86400)}d` }, // < 7d
  { s: 2628000, label: v => `${Math.floor(v/604800)}w` }, // < ~1mo
  { s: 31536000, label: v => `${Math.floor(v/2628000)}mo` }, // < 1y
  { s: Infinity, label: v => `${Math.floor(v/31536000)}y` }
];

const relativeTime = (date) => {
  if (!date) return '';
  const d = (date.toDate?.() || (typeof date === 'string' ? new Date(date) : date));
  const diff = Math.max(0, (Date.now() - d.getTime()) / 1000);
  const bucket = RELATIVE_THRESHOLDS.find(b => diff < b.s);
  return bucket ? bucket.label(diff) : '';
};

const formatExact = (date) => {
  const d = (date?.toDate?.() || (typeof date === 'string' ? new Date(date) : date));
  if (!d) return '';
  return d.toLocaleString();
};

const debounce = (fn, wait=300) => {
  let t;
  const wrapped = (...a) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...a), wait);
  };
  wrapped.cancel = () => clearTimeout(t);
  return wrapped;
};

/* Group notifications by date bucket */
const groupByBucket = (list) => {
  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const todayStart = startOfDay(now).getTime();
  const yesterdayStart = todayStart - 86400000;
  const weekStart = todayStart - 6 * 86400000; // last 7 days window

  const buckets = {
    'Today': [],
    'Yesterday': [],
    'This Week': [],
    'Earlier': []
  };

  list.forEach(n => {
    const ts = n.createdAt?.toDate?.() ? n.createdAt.toDate() :
      (typeof n.createdAt === 'string' ? new Date(n.createdAt) : n.createdAt);
    if (!ts) {
      buckets['Earlier'].push(n);
      return;
    }
    const t = ts.getTime();
    if (t >= todayStart) buckets['Today'].push(n);
    else if (t >= yesterdayStart) buckets['Yesterday'].push(n);
    else if (t >= weekStart) buckets['This Week'].push(n);
    else buckets['Earlier'].push(n);
  });
  return buckets;
};

/* ---------------- Main Component ---------------- */
export default function NotificationPage() {
  const [user, setUser] = useState(null);
  const [initialLoaded, setInitialLoaded] = useState(false);

  const [notifications, setNotifications] = useState([]);          // All loaded (paged + live)
  const [liveIds, setLiveIds] = useState(new Set());               // Track live snapshot doc IDs to avoid duplicates
  const [loadingPage, setLoadingPage] = useState(false);
  const [endReached, setEndReached] = useState(false);

  /* Search & Filters */
  const [search, setSearch] = useState('');
  const searchRef = useRef();
  const debouncedSearch = useRef();

  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [importanceFilter, setImportanceFilter] = useState('all');  // all | high | normal | low
  const [statusFilter, setStatusFilter] = useState('all');          // all | unread | read | archived | starred
  const [density, setDensity] = useState('comfortable');            // comfortable | compact

  /* Selection */
  const [selected, setSelected] = useState(new Set());

  /* Online status */
  const [online, setOnline] = useState(navigator.onLine);

  /* Pagination cursor */
  const lastDocRef = useRef(null);

  /* Real-time unsub */
  const liveUnsubRef = useRef(null);

  /* Auth */
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(u => {
      setUser(u);
    });
    return () => unsubscribe();
  }, []);

  /* Monitor online/offline */
  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  /* Debounced search input */
  useEffect(() => {
    debouncedSearch.current = debounce(val => setSearch(val.toLowerCase()), 300);
    return () => debouncedSearch.current?.cancel?.();
  }, []);

  /* Initial load (paged) + attach live listener for newest */
  useEffect(() => {
    if (!user) return;
    let ignore = false;

    const loadInitial = async () => {
      setLoadingPage(true);
      try {
        const baseQ = query(
          collection(db, 'Notifications'),
          where('to', '==', user.uid),
          orderBy('createdAt', 'desc'),
          limit(25)
        );
        const snap = await getDocs(baseQ);

        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        if (!ignore) {
          setNotifications(docs);
          setEndReached(snap.size < 25);
          lastDocRef.current = snap.docs[snap.docs.length - 1] || null;
          setInitialLoaded(true);
        }

        // Set up live listener for "new" notifications (those newer than first loaded)
        if (liveUnsubRef.current) liveUnsubRef.current();
        const newestTS = docs[0]?.createdAt || serverTimestamp();
        const liveQ = query(
          collection(db, 'Notifications'),
            where('to', '==', user.uid),
            orderBy('createdAt', 'desc'),
            limit(25)
        );
        liveUnsubRef.current = onSnapshot(liveQ, liveSnap => {
          // Merge in (preferring newest)
            const liveDocs = [];
            liveSnap.docChanges().forEach(change => {
              if (change.type === 'added') {
                const data = { id: change.doc.id, ...change.doc.data() };
                liveDocs.push(data);
              } else if (change.type === 'modified') {
                setNotifications(prev =>
                  prev.map(n => n.id === change.doc.id ? { id: change.doc.id, ...change.doc.data() } : n)
                );
              } else if (change.type === 'removed') {
                setNotifications(prev => prev.filter(n => n.id !== change.doc.id));
              }
            });
            if (liveDocs.length) {
              setNotifications(prev => {
                const existingIds = new Set(prev.map(p => p.id));
                const merged = [...liveDocs.filter(ld => !existingIds.has(ld.id)), ...prev];
                return merged.sort((a,b) => {
                  const at = a.createdAt?.toDate?.() ? a.createdAt.toDate().getTime() :
                    (typeof a.createdAt === 'string' ? Date.parse(a.createdAt) : 0);
                  const bt = b.createdAt?.toDate?.() ? b.createdAt.toDate().getTime() :
                    (typeof b.createdAt === 'string' ? Date.parse(b.createdAt) : 0);
                  return bt - at;
                });
              });
            }
        });

      } catch (err) {
        console.error(err);
        toast.error('Failed to load notifications');
      } finally {
        if (!ignore) setLoadingPage(false);
      }
    };
    loadInitial();

    return () => {
      ignore = true;
      liveUnsubRef.current?.();
    };
  }, [user]);

  /* Load more pagination */
  const loadMore = useCallback(async () => {
    if (!user || endReached || !lastDocRef.current) return;
    setLoadingPage(true);
    try {
      const moreQ = query(
        collection(db, 'Notifications'),
        where('to', '==', user.uid),
        orderBy('createdAt', 'desc'),
        startAfter(lastDocRef.current),
        limit(25)
      );
      const snap = await getDocs(moreQ);
      if (snap.empty) {
        setEndReached(true);
      } else {
        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setNotifications(prev => {
          const existing = new Set(prev.map(p => p.id));
          const merged = [...prev, ...docs.filter(x => !existing.has(x.id))];
          return merged;
        });
        lastDocRef.current = snap.docs[snap.docs.length - 1];
        if (snap.size < 25) setEndReached(true);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load more');
    } finally {
      setLoadingPage(false);
    }
  }, [user, endReached]);

  /* Filtering & Searching */
  const filtered = useMemo(() => {
    let list = notifications;

    // statusFilter
    if (statusFilter === 'unread') list = list.filter(n => !n.read);
    else if (statusFilter === 'read') list = list.filter(n => n.read);
    else if (statusFilter === 'archived') list = list.filter(n => n.archived);
    else if (statusFilter === 'starred') list = list.filter(n => n.starred);

    if (importanceFilter !== 'all') {
      list = list.filter(n => (n.importance || 'normal') === importanceFilter);
    }

    if (search) {
      list = list.filter(n => {
        const hay = (n.message + ' ' + (n.type || '') + ' ' + (n.fromEmail || '')).toLowerCase();
        return hay.includes(search);
      });
    }
    return list;
  }, [notifications, statusFilter, importanceFilter, search]);

  const grouped = useMemo(() => groupByBucket(filtered), [filtered]);

  /* ---- Selection logic ---- */
  const toggleSelect = useCallback(id => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  const selectVisible = useCallback(() => {
    setSelected(new Set(filtered.map(n => n.id)));
  }, [filtered]);

  const toggleSelectAllShortcut = useCallback(() => {
    setSelected(prev => {
      if (prev.size === filtered.length) return new Set();
      return new Set(filtered.map(n => n.id));
    });
  }, [filtered]);

  /* ---- Keyboard shortcuts ---- */
  useEffect(() => {
    const handler = (e) => {
      if (e.key === '/' && document.activeElement !== searchRef.current) {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === 'Escape') {
        clearSelection();
      } else if (e.key.toLowerCase() === 'a' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggleSelectAllShortcut();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toggleSelectAllShortcut, clearSelection]);

  /* ---- Mutations ---- */
  const batchUpdate = useCallback(async (ids, data, successMsg) => {
    if (!ids.length) {
      toast.info('No notifications selected');
      return;
    }
    try {
      const batch = writeBatch(db);
      ids.forEach(id => batch.update(doc(db, 'Notifications', id), data));
      await batch.commit();
      setNotifications(prev => prev.map(n => ids.includes(n.id) ? { ...n, ...data } : n));
      toast.success(successMsg);
      if (data.archived) {
        setSelected(s => {
          const next = new Set(s);
          ids.forEach(i => next.delete(i));
          return next;
        });
      }
    } catch (err) {
      console.error(err);
      toast.error('Update failed');
    }
  }, []);

  const markRead = useCallback(() => {
    batchUpdate([...selected], { read: true }, 'Marked as read');
  }, [selected, batchUpdate]);

  const markUnread = useCallback(() => {
    batchUpdate([...selected], { read: false }, 'Marked as unread');
  }, [selected, batchUpdate]);

  const archiveSelected = useCallback(() => {
    batchUpdate([...selected], { archived: true }, 'Archived');
  }, [selected, batchUpdate]);

  const unarchiveSelected = useCallback(() => {
    batchUpdate([...selected], { archived: false }, 'Unarchived');
  }, [selected, batchUpdate]);

  const toggleStar = useCallback(id => {
    const n = notifications.find(n => n.id === id);
    if (!n) return;
    const newVal = !n.starred;
    updateDoc(doc(db, 'Notifications', id), { starred: newVal })
      .then(() => {
        setNotifications(prev => prev.map(x => x.id === id ? { ...x, starred: newVal } : x));
      })
      .catch(() => toast.error('Star toggle failed'));
  }, [notifications]);

  const starSelected = useCallback(() => {
    batchUpdate([...selected], { starred: true }, 'Starred');
  }, [selected, batchUpdate]);

  const unstarSelected = useCallback(() => {
    batchUpdate([...selected], { starred: false }, 'Unstarred');
  }, [selected, batchUpdate]);

  // OPTIONAL PERMANENT DELETE (disabled by default)
  const PERMANENT_DELETE_ENABLED = false;
  const deleteSelected = useCallback(async () => {
    if (!PERMANENT_DELETE_ENABLED) {
      toast.info('Permanent delete disabled (use archive)');
      return;
    }
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} notification(s)? This cannot be undone.`)) return;
    try {
      // Could do batch delete—Firestore now has recursive / new Admin features; here update approach for safety
      toast.info('Deleting not implemented in this snippet');
    } catch (err) {
      toast.error('Delete failed');
    }
  }, [selected]);

  const handleRowClick = useCallback((id) => {
    // Mark read on click
    const n = notifications.find(n => n.id === id);
    if (!n || n.read) return;
    updateDoc(doc(db, 'Notifications', id), { read: true })
      .then(() => setNotifications(prev => prev.map(p => p.id === id ? { ...p, read: true } : p)))
      .catch(() => {/* silent */});
  }, [notifications]);

  /* ---- Density toggle ---- */
  const toggleDensity = () => setDensity(d => d === 'comfortable' ? 'compact' : 'comfortable');

  /* ---- Clear filters ---- */
  const resetFilters = () => {
    setSearch('');
    searchRef.current.value = '';
    setStatusFilter('all');
    setImportanceFilter('all');
  };

  /* ---- Derived counts ---- */
  const unreadCount = notifications.filter(n => !n.read && !n.archived).length;

  /* ---------------- Render Helpers ---------------- */
  const EmptyState = () => (
    <div className="notif-empty">
      <FaInbox size={44} />
      <h3>No notifications</h3>
      <p>Everything looks quiet. You’ll see updates here as they arrive.</p>
    </div>
  );

  const BucketSection = ({ title, items }) => {
    if (!items.length) return null;
    return (
      <div className="bucket">
        <h4 className="bucket-title">{title}</h4>
        <ul className={`notif-list ${density}`}>
          {items.map(n => (
            <li
              key={n.id}
              className={`notif-row ${n.read ? '' : 'unread'} ${n.archived ? 'archived' : ''}`}
            >
              <div className="sel-col">
                <input
                  type="checkbox"
                  checked={selected.has(n.id)}
                  onChange={() => toggleSelect(n.id)}
                  aria-label="Select notification"
                />
              </div>

              <button
                className="star-btn"
                onClick={() => toggleStar(n.id)}
                title={n.starred ? 'Unstar' : 'Star'}
                aria-label={n.starred ? 'Unstar notification' : 'Star notification'}
              >
                {n.starred ? <FaStar /> : <FaRegStar />}
              </button>

              <div
                className="notif-main"
                onClick={() => handleRowClick(n.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleRowClick(n.id)}
              >
                <div className="notif-top-line">
                  <span className="message">
                    {renderImportanceIcon(n.importance)}
                    {n.message}
                    {n.actionUrl && (
                      <a
                        href={n.actionUrl}
                        className="action-link"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        aria-label="Open linked resource"
                      >
                        <FaLink />
                      </a>
                    )}
                  </span>
                  <time
                    className="time"
                    title={formatExact(n.createdAt)}
                    dateTime={formatExact(n.createdAt)}
                  >
                    {relativeTime(n.createdAt)}
                  </time>
                </div>

                <div className="notif-meta">
                  <span className="badge type">
                    {n.type || 'General'}
                  </span>
                  {n.archived && <span className="badge archived-flag">Archived</span>}
                  {n.starred && <span className="badge starred-flag">Starred</span>}
                  <span className={`badge imp-${n.importance || 'normal'}`}>
                    {(n.importance || 'normal').toUpperCase()}
                  </span>
                </div>
              </div>

              {!n.read && (
                <div className="unread-dot" title="Unread" />
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderImportanceIcon = (imp) => {
    if (imp === 'high') return <FaExclamationTriangle className="imp-icon high" title="High importance" />;
    if (imp === 'low') return <FaGlobeAmericas className="imp-icon low" title="Low importance" />;
    return <FaBolt className="imp-icon normal" title="Normal importance" />;
  };

  return (
    <div className="notification-page">
      <ToastContainer position="top-right" autoClose={2500} />
      <Sidebar />
      <main className="notification-main">
        <div className="top-bar">
          <div className="heading-wrap">
            <h1><FaBell /> Notifications</h1>
            <span className="sub">Unread: {unreadCount}</span>
            {!online && <span className="offline-badge">Offline</span>}
          </div>

            <div className="actions-wrap">
              <button
                className="btn density"
                onClick={toggleDensity}
                title={density === 'comfortable' ? 'Switch to compact density' : 'Switch to comfortable density'}
              >
                {density === 'comfortable' ? <FaCompressArrowsAlt /> : <FaExpandArrowsAlt />}
              </button>
              <button
                className="btn"
                onClick={() => { lastDocRef.current = null; setNotifications([]); setInitialLoaded(false); setEndReached(false); }}
                title="Force reload (will re-query)"
              >
                <FaSync />
              </button>
              <button
                className={`btn filter-toggle ${filterPanelOpen ? 'open' : ''}`}
                onClick={() => setFilterPanelOpen(o => !o)}
                aria-expanded={filterPanelOpen}
                aria-controls="filter-panel"
              >
                <FaFilter /> {filterPanelOpen ? 'Hide Filters' : 'Show Filters'}
                {filterPanelOpen ? <FaChevronUp /> : <FaChevronDown />}
              </button>
            </div>
        </div>

        <div className="search-row">
          <div className="search-box">
            <FaSearch />
            <input
              ref={searchRef}
              placeholder="Search notifications… ( / )"
              onChange={e => debouncedSearch.current(e.target.value)}
              aria-label="Search notifications"
            />
            {search && (
              <button
                className="clear-search"
                onClick={() => { searchRef.current.value=''; setSearch(''); }}
                aria-label="Clear search"
              >
                <FaTimes />
              </button>
            )}
          </div>
          <div className="bulk-info">
            {selected.size > 0 && (
              <span>{selected.size} selected</span>
            )}
          </div>
        </div>

        {filterPanelOpen && (
          <div className="filter-panel" id="filter-panel">
            <div className="filter-group">
              <label>Status</label>
              <div className="chip-row">
                {['all','unread','read','starred','archived'].map(f => (
                  <button
                    key={f}
                    className={`chip ${statusFilter === f ? 'active' : ''}`}
                    onClick={() => setStatusFilter(f)}
                  >
                    {f === 'all' ? 'All' :
                     f === 'unread' ? 'Unread' :
                     f === 'read' ? 'Read' :
                     f === 'starred' ? 'Starred' : 'Archived'}
                  </button>
                ))}
              </div>
            </div>

            <div className="filter-group">
              <label>Importance</label>
              <div className="chip-row">
                {['all','high','normal','low'].map(f => (
                  <button
                    key={f}
                    className={`chip ${importanceFilter === f ? 'active' : ''}`}
                    onClick={() => setImportanceFilter(f)}
                  >
                    {f.charAt(0).toUpperCase()+f.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="filter-actions">
              <button className="btn reset" onClick={resetFilters}>Reset Filters</button>
            </div>
          </div>
        )}

        {/* Bulk action toolbar */}
        {selected.size > 0 && (
          <div className="bulk-toolbar" role="toolbar" aria-label="Bulk actions">
            <button onClick={markRead} className="btn" aria-label="Mark as read">
              <FaCheckCircle /> Read
            </button>
            <button onClick={markUnread} className="btn" aria-label="Mark as unread">
              <FaBell /> Unread
            </button>
            <button onClick={starSelected} className="btn" aria-label="Star selected">
              <FaStar /> Star
            </button>
            <button onClick={unstarSelected} className="btn" aria-label="Unstar selected">
              <FaRegStar /> Unstar
            </button>
            <button onClick={archiveSelected} className="btn" aria-label="Archive selected">
              <FaArchive /> Archive
            </button>
            <button onClick={unarchiveSelected} className="btn" aria-label="Unarchive selected">
              <FaInbox /> Unarchive
            </button>
            {PERMANENT_DELETE_ENABLED && (
              <button onClick={deleteSelected} className="btn danger" aria-label="Delete selected">
                <FaTrashAlt /> Delete
              </button>
            )}
            <button onClick={clearSelection} className="btn light" aria-label="Clear selection">
              <FaTimes /> Clear
            </button>
            <button onClick={selectVisible} className="btn light" aria-label="Select all filtered">
              Select All
            </button>
          </div>
        )}

        {/* Content */}
        {!initialLoaded && loadingPage && (
          <div className="skeleton-block">
            {[...Array(6)].map((_,i) => <div key={i} className="notif-skel" />)}
          </div>
        )}

        {initialLoaded && filtered.length === 0 && !loadingPage && <EmptyState />}

        {initialLoaded && filtered.length > 0 && (
          <div className="buckets-wrapper">
            <BucketSection title="Today" items={grouped['Today']} />
            <BucketSection title="Yesterday" items={grouped['Yesterday']} />
            <BucketSection title="This Week" items={grouped['This Week']} />
            <BucketSection title="Earlier" items={grouped['Earlier']} />
          </div>
        )}

        {/* Load more */}
        {initialLoaded && !endReached && (
          <div className="load-more-wrap">
            <button
              className="btn load-more"
              disabled={loadingPage}
              onClick={loadMore}
            >
              {loadingPage ? 'Loading…' : 'Load More'}
            </button>
          </div>
        )}

        {endReached && filtered.length > 0 && (
          <div className="end-indicator">
            <span>No more notifications</span>
          </div>
        )}

        {/* Floating offline banner */}
        {!online && (
          <div className="offline-banner" role="status">
            <FaExclamationCircle /> You are offline. Viewing cached data.
          </div>
        )}
      </main>
    </div>
  );
}
